// PRD 3.3: 富媒体与 Markdown 渲染
// 组合 marked + DOMPurify + highlight.js + KaTeX，输出安全的 HTML 字符串
import { marked } from 'marked';
import markedKatex from 'marked-katex-extension';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js/lib/common';
import 'katex/dist/katex.min.css'; // Vite 自动打包

// 启用 $...$ / $$...$$ 数学公式支持
// throwOnError:false 让流式过程中不完整的公式降级为文本而非抛错
// nonStandard:true 允许 $ 前后紧贴中文/标点（标准模式要求空格分隔，中文语境下会漏匹配）
marked.use(
  markedKatex({
    throwOnError: false,
    output: 'html',
    nonStandard: true,
  }),
);

// GFM + 换行即 <br>，对话场景下更自然
marked.setOptions({
  gfm: true,
  breaks: true,
});

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * 将 Markdown 文本渲染为已消毒的 HTML 字符串。
 * 流式渲染过程中可对不完整 Markdown 安全降级。
 * @param {string} text
 * @returns {string} 安全 HTML
 */
export function renderMarkdown(text) {
  let raw;
  try {
    raw = marked.parse(text ?? '');
  } catch {
    raw = `<p>${escapeHtml(String(text ?? ''))}</p>`;
  }
  return DOMPurify.sanitize(raw, {
    // 允许链接 target=_blank + 图片懒加载属性
    ADD_ATTR: ['target', 'rel', 'loading', 'decoding'],
    // KaTeX 输出含 MathML 元素，需开启 mathMl profile 避免被删除
    USE_PROFILES: { html: true, mathMl: true, svg: true },
  });
}

/**
 * 后处理已渲染的 DOM 节点：
 *  - 代码块语法高亮（PRD 3.3）
 *  - 一键复制按钮（PRD 3.3）
 * 应在流式 done 之后调用一次，避免流式过程中重复高亮的性能开销。
 * @param {HTMLElement} rootEl
 */
export function enhanceMessageEl(rootEl) {
  // 语法高亮
  rootEl.querySelectorAll('pre code').forEach((code) => {
    if (code.dataset.highlighted) return;
    try {
      hljs.highlightElement(code);
      code.dataset.highlighted = 'true';
    } catch {
      /* 忽略未知语言 */
    }
  });

  // 一键复制按钮
  rootEl.querySelectorAll('pre').forEach((pre) => {
    if (pre.querySelector('.code-copy-btn')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'code-copy-btn';
    btn.textContent = 'copy';
    btn.setAttribute('aria-label', '复制代码');
    btn.addEventListener('click', async () => {
      const code = pre.querySelector('code');
      const text = code ? code.innerText : pre.innerText;
      try {
        await navigator.clipboard.writeText(text);
        btn.textContent = 'copied';
      } catch {
        btn.textContent = 'failed';
      }
      setTimeout(() => {
        btn.textContent = 'copy';
      }, 1500);
    });
    pre.appendChild(btn);
  });

  // 链接：外链新标签打开 + 外链图标（PRD 3.3）
  rootEl.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    const isExternal = !href.startsWith('/') && !href.startsWith('#') && !href.startsWith(window.location.origin);
    if (isExternal) {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    }
    if (!a.querySelector('.link-icon')) {
      const icon = document.createElement('span');
      icon.className = 'link-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = ' ↗';
      a.appendChild(icon);
    }
  });

  // 图片：懒加载 + 占位符状态机（PRD 3.3）
  // - loading：灰度 + 虚线边框 + alt 占位
  // - loaded：恢复彩色 + 实线边框
  // - error：红框 + 失败文案 + 原 URL
  rootEl.querySelectorAll('img').forEach((img) => {
    if (img.dataset.imgEnhanced === 'true') return;
    img.dataset.imgEnhanced = 'true';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.dataset.state = 'loading';

    img.addEventListener('load', () => {
      img.dataset.state = 'loaded';
    });
    img.addEventListener('error', () => {
      img.dataset.state = 'error';
      img.alt = img.alt || '图片加载失败';
      img.title = img.src;
    });
  });

  // 表格：包裹 overflow-x 容器以支持宽表横向滚动（PRD 3.3）
  // marked 直接输出 <table>，<table> 本身不能 display:block，否则 thead/tbody 宽度无法对齐
  rootEl.querySelectorAll('table').forEach((table) => {
    if (table.parentElement.classList.contains('table-wrap')) return;
    const wrap = document.createElement('div');
    wrap.className = 'table-wrap';
    table.parentElement.insertBefore(wrap, table);
    wrap.appendChild(table);
  });
}
